import React from 'react';
import { Loader2, Camera, Calculator, MessageSquare, Paperclip } from 'lucide-react';
import { moduleApi } from '../../lib/api';

// Ce qu'il faut voir pour VALIDER une demande de réservation en ligne (lot C, 2026-09-10) : les photos
// jointes par le visiteur, son message, et l'estimation que le simulateur lui avait affichée. Posé en
// tête de la modale « Confirmer la demande de réservation » — l'établissement vérifie sur la photo que
// la prestation choisie est la bonne, et compare au montant vu par le client, avant d'assigner un salarié.
interface Origine {
    photos: { index: number; name: string; data_url: string }[];
    fichiers: { index: number; name: string }[];
    message: string;
    estimation: { total_ttc: number; created_at: string; lines: { id: string; qty: number; label: string; unit_price_ttc: number }[] } | null;
}

const eur = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })} €`;

export function OrigineDemande({ appointmentId }: { appointmentId: string }) {
    const [origine, setOrigine] = React.useState<Origine | null>(null);
    const [chargement, setChargement] = React.useState(true);
    const [agrandie, setAgrandie] = React.useState<string | null>(null);

    React.useEffect(() => {
        let vivant = true;
        moduleApi.getPrestationAppointmentOrigine(appointmentId)
            .then((r: any) => { if (vivant && r && !r.error) setOrigine(r); })
            .catch(() => {})
            .finally(() => { if (vivant) setChargement(false); });
        return () => { vivant = false; };
    }, [appointmentId]);

    if (chargement) return <div className="mx-6 mt-4 flex items-center gap-2 text-xs text-content-muted"><Loader2 size={13} className="animate-spin" /> Lecture de la demande…</div>;
    if (!origine || (!origine.photos.length && !origine.message && !origine.estimation && !origine.fichiers.length)) return null;

    return (
        <div className="mx-6 mt-4 p-4 rounded-2xl bg-surface-soft border border-border space-y-4">
            {agrandie && (
                <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setAgrandie(null)}>
                    <img src={agrandie} alt="" className="max-w-full max-h-full rounded-xl object-contain" />
                </div>
            )}

            {origine.estimation && (
                <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Calculator size={12} /> Ce que le simulateur lui a affiché</p>
                    <div className="rounded-xl bg-surface border border-border divide-y divide-border">
                        {origine.estimation.lines.map((l) => (
                            <div key={l.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs">
                                <span className="text-content truncate">{l.label}</span>
                                <span className="text-content-muted shrink-0 tabular-nums">{l.qty} × {eur(l.unit_price_ttc)}</span>
                            </div>
                        ))}
                        <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm font-bold">
                            <span className="text-content">Estimation vue par le client</span>
                            <span className="text-content tabular-nums">{eur(origine.estimation.total_ttc)}</span>
                        </div>
                    </div>
                    <p className="text-[11px] text-content-muted">Les lignes ci-dessous sont celles de sa demande. Si la photo montre autre chose, corrigez-les avant de valider : le client recevra le montant corrigé.</p>
                </div>
            )}

            {origine.photos.length > 0 && (
                <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Camera size={12} /> Photo{origine.photos.length > 1 ? 's' : ''} jointe{origine.photos.length > 1 ? 's' : ''} par le client</p>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                        {origine.photos.map((p) => (
                            <button key={p.index} type="button" onClick={() => setAgrandie(p.data_url)}
                                className="aspect-[4/3] rounded-lg overflow-hidden border border-border bg-surface cursor-zoom-in">
                                <img src={p.data_url} alt={p.name} className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-content-muted">Cliquez pour agrandir. Vérifiez que la prestation demandée correspond à ce que montre la photo.</p>
                </div>
            )}

            {origine.fichiers.length > 0 && (
                <p className="text-[11px] text-content-muted flex items-center gap-1.5"><Paperclip size={12} /> {origine.fichiers.map((f) => f.name).join(', ')}</p>
            )}

            {origine.message && (
                <div className="space-y-1">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><MessageSquare size={12} /> Son message</p>
                    <p className="text-sm text-content whitespace-pre-wrap">{origine.message}</p>
                </div>
            )}
        </div>
    );
}
